/**
 * Offline-aware API client
 * Wraps axios to provide offline support with caching and sync queue
 */

import axios from 'axios';
import { 
  initOfflineDB, 
  cacheApiResponse, 
  getCachedResponse, 
  addToSyncQueue,
  getPendingSyncOperations,
  removeFromSyncQueue,
  isOnline 
} from './offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache TTL for different endpoints (in milliseconds)
const CACHE_TTL = {
  '/api/customers': 10 * 60 * 1000,      // 10 minutes
  '/api/audits': 10 * 60 * 1000,          // 10 minutes
  '/api/users': 10 * 60 * 1000,           // 10 minutes
  '/api/reports': 5 * 60 * 1000,          // 5 minutes
  '/api/interactive-reports': 5 * 60 * 1000,
  '/api/templates': 30 * 60 * 1000,       // 30 minutes
  '/api/dashboard/stats': 2 * 60 * 1000,  // 2 minutes
  'default': 5 * 60 * 1000                // 5 minutes default
};

/**
 * Get cache TTL for a URL
 */
const getCacheTTL = (url) => {
  for (const [pattern, ttl] of Object.entries(CACHE_TTL)) {
    if (url.includes(pattern)) {
      return ttl;
    }
  }
  return CACHE_TTL.default;
};

/**
 * Create offline-aware axios instance
 */
const offlineApi = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Request interceptor
offlineApi.interceptors.request.use(
  async (config) => {
    // Initialize DB on first request
    await initOfflineDB();
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - cache successful GET responses
offlineApi.interceptors.response.use(
  async (response) => {
    // Cache GET requests
    if (response.config.method === 'get' && response.status === 200) {
      const cacheKey = response.config.url;
      const ttl = getCacheTTL(cacheKey);
      await cacheApiResponse(cacheKey, response.data, ttl);
    }
    return response;
  },
  async (error) => {
    // If offline and GET request, try to return cached data
    if (!isOnline() && error.config?.method === 'get') {
      const cacheKey = error.config.url;
      const cachedData = await getCachedResponse(cacheKey);
      
      if (cachedData) {
        console.log('Returning cached data for:', cacheKey);
        return { 
          data: cachedData, 
          status: 200, 
          statusText: 'OK (Cached)',
          config: error.config,
          headers: {},
          cached: true 
        };
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Offline-aware GET request
 */
export const offlineGet = async (url, config = {}) => {
  try {
    // Try network first
    if (isOnline()) {
      return await offlineApi.get(url, config);
    }
  } catch (error) {
    // Network failed, try cache
    console.log('Network request failed, trying cache');
  }
  
  // Offline or network failed - try cache
  const cachedData = await getCachedResponse(url);
  if (cachedData) {
    return { 
      data: cachedData, 
      status: 200, 
      cached: true 
    };
  }
  
  throw new Error('No network connection and no cached data available');
};

/**
 * Offline-aware POST request with sync queue
 */
export const offlinePost = async (url, data, config = {}) => {
  if (isOnline()) {
    try {
      const response = await offlineApi.post(url, data, config);
      return response;
    } catch (error) {
      // If server error, don't queue
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }
      // Network error - queue for later
    }
  }
  
  // Offline or network error - add to sync queue
  const queueId = await addToSyncQueue({
    type: 'POST',
    url,
    data,
    config
  });
  
  console.log('Request queued for sync:', queueId);
  return { 
    data: { queued: true, queueId }, 
    status: 202,
    queued: true 
  };
};

/**
 * Offline-aware PUT request with sync queue
 */
export const offlinePut = async (url, data, config = {}) => {
  if (isOnline()) {
    try {
      const response = await offlineApi.put(url, data, config);
      return response;
    } catch (error) {
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }
    }
  }
  
  const queueId = await addToSyncQueue({
    type: 'PUT',
    url,
    data,
    config
  });
  
  return { 
    data: { queued: true, queueId }, 
    status: 202,
    queued: true 
  };
};

/**
 * Offline-aware DELETE request with sync queue
 */
export const offlineDelete = async (url, config = {}) => {
  if (isOnline()) {
    try {
      const response = await offlineApi.delete(url, config);
      return response;
    } catch (error) {
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }
    }
  }
  
  const queueId = await addToSyncQueue({
    type: 'DELETE',
    url,
    config
  });
  
  return { 
    data: { queued: true, queueId }, 
    status: 202,
    queued: true 
  };
};

/**
 * Sync all pending operations
 */
export const syncPendingOperations = async () => {
  if (!isOnline()) {
    console.log('Cannot sync - offline');
    return { synced: 0, failed: 0 };
  }
  
  const pending = await getPendingSyncOperations();
  console.log(`Syncing ${pending.length} pending operations`);
  
  let synced = 0;
  let failed = 0;
  
  for (const operation of pending) {
    try {
      switch (operation.type) {
        case 'POST':
          await offlineApi.post(operation.url, operation.data, operation.config);
          break;
        case 'PUT':
          await offlineApi.put(operation.url, operation.data, operation.config);
          break;
        case 'DELETE':
          await offlineApi.delete(operation.url, operation.config);
          break;
        default:
          console.warn('Unknown operation type:', operation.type);
      }
      
      await removeFromSyncQueue(operation.id);
      synced++;
    } catch (error) {
      console.error('Failed to sync operation:', operation, error);
      failed++;
    }
  }
  
  console.log(`Sync complete: ${synced} synced, ${failed} failed`);
  return { synced, failed };
};

/**
 * Setup online/offline event listeners
 */
export const setupOfflineListeners = (onOnline, onOffline, onSyncComplete) => {
  const handleOnline = async () => {
    console.log('Back online - syncing...');
    if (onOnline) onOnline();
    
    const result = await syncPendingOperations();
    if (onSyncComplete) onSyncComplete(result);
  };
  
  const handleOffline = () => {
    console.log('Gone offline');
    if (onOffline) onOffline();
  };
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

export default offlineApi;
